import React, { useEffect, useState } from "react";
import { Container, Grid, Typography, Chip, Button } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { authAxios, authRequestHeaders, useAuth } from "../../contexts/AuthContext";
import PageLoader from "../../components/PageLoader/PageLoader";
import RequireAuth from "../../components/RequireAuth/RequireAuth";
import {
  isRankingPublished,
  isCompetitionEntry,
  getCompetitionScoreSummary,
} from "../../utils/independentRanking";
import { TOP_64_COMPETITION_SHORT_LABEL } from "../../constants/top64Competition";
import "./YourRankings.css";

function formatAuthError(detail) {
  if (typeof detail !== "string") {
    return "Could not load your rankings. Please refresh the page.";
  }
  if (detail.startsWith("auth_failed:")) {
    const code = detail.replace("auth_failed:", "");
    const messages = {
      missing_token: "Sign in required (no access token was sent).",
      jwt_expired: "Session expired. Please sign in again.",
      jwt_invalid: "Invalid session. Please sign in again.",
      jwt_missing_sub: "Invalid session. Please sign in again.",
      user_not_found: "Account not found on this server. Try signing out and back in.",
      user_inactive: "Your account is disabled.",
    };
    return messages[code] || `Authentication failed (${code}). Try signing in again.`;
  }
  return detail;
}

function YourRankingsContent() {
  const { loading: authLoading, ensureAccessToken } = useAuth();
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;

    async function loadRankings() {
      setLoading(true);
      setLoadError("");

      const token = await ensureAccessToken();
      if (cancelled) return;

      if (!token) {
        setRankings([]);
        setLoadError("Session expired. Please sign in again.");
        setLoading(false);
        return;
      }

      try {
        const res = await authAxios.get("/independent_rankings/mine", {
          headers: authRequestHeaders(token),
        });
        if (!cancelled) setRankings(res.data || []);
      } catch (err) {
        if (!cancelled) {
          setRankings([]);
          const detail = err.response?.data?.detail;
          console.warn("[YourRankings] load failed", err.response?.status, detail);
          setLoadError(formatAuthError(detail));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRankings();

    return () => {
      cancelled = true;
    };
  }, [authLoading, ensureAccessToken]);

  const handleDelete = async (ranking) => {
    if (isRankingPublished(ranking)) {
      window.alert("Published rankings cannot be deleted.");
      return;
    }

    const confirmed = window.confirm(
      `Delete "${ranking.title}"? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(ranking.independent_ranking_id);
    try {
      await authAxios.delete(`/independent_rankings/${ranking.independent_ranking_id}`);
      setRankings((prev) =>
        prev.filter((r) => r.independent_ranking_id !== ranking.independent_ranking_id)
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      window.alert(
        typeof detail === "string"
          ? detail
          : "Failed to delete ranking. Please try again."
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Your Rankings - Hockey Pipeline</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageLoader loading={loading} />
      <div className="your-rankings-shell top-container">
      <Container className="container your-rankings-page">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h4" component="div" className="page-title">
              Your Rankings
            </Typography>
            <div className="title-underline" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ color: "var(--text-muted)", mb: 2 }}>
              Manage your independent prospect rankings. Drafts are only visible to you
              until you publish them. Published rankings cannot be edited or deleted.
            </Typography>
          </Grid>
          {!loading && loadError && (
            <Grid item xs={12}>
              <Typography sx={{ color: "var(--negative)" }}>{loadError}</Typography>
            </Grid>
          )}
          {!loading && !loadError && rankings.length === 0 && (
            <Grid item xs={12}>
              <Typography sx={{ color: "var(--text-muted)" }}>
                You have not created any rankings yet.{" "}
                <Link to="/your-rankings/create" className="link-default">
                  Create one
                </Link>
                .
              </Typography>
            </Grid>
          )}
          {rankings.length > 0 && (
            <Grid item xs={12}>
              <div className="your-rankings-list">
                {rankings.map((r) => {
                  const isDraft = Boolean(r.draft);
                  const published = isRankingPublished(r);
                  const isCompetition = isCompetitionEntry(r);
                  const editPath = `/your-rankings/${r.independent_ranking_id}/edit`;
                  const viewPath = `/independent-rankings/${r.independent_ranking_id}`;
                  const isDeleting = deletingId === r.independent_ranking_id;
                  const competitionScore = getCompetitionScoreSummary(r);

                  return (
                    <article
                      key={r.independent_ranking_id}
                      className={[
                        "your-ranking-card",
                        isDraft ? "your-ranking-card--draft" : "",
                        isCompetition ? "your-ranking-card--competition" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className="your-ranking-card-main">
                        {(isDraft || isCompetition) && (
                          <div className="your-ranking-card-header">
                            {isDraft && (
                              <span className="your-ranking-draft-badge">Draft</span>
                            )}
                            {isCompetition && (
                              <span className="your-ranking-competition-badge">
                                {TOP_64_COMPETITION_SHORT_LABEL}
                              </span>
                            )}
                          </div>
                        )}
                        {isDraft ? (
                          <>
                            <Typography component="h2" className="your-ranking-card-title">
                              {r.title}
                            </Typography>
                            <p className="your-ranking-draft-hint">
                              {isCompetition
                                ? "Competition entry — private until entries are released after the deadline."
                                : "Not published yet — only you can see this ranking."}
                            </p>
                          </>
                        ) : (
                          <Typography
                            component={Link}
                            to={viewPath}
                            className="your-ranking-card-title"
                          >
                            {r.title}
                          </Typography>
                        )}
                        <div className="your-ranking-card-meta">
                          <span className="your-ranking-card-caption">
                            Draft {r.draft_year}
                            {r.creation_date &&
                              ` · ${format(new Date(r.creation_date), "MMM d, yyyy")}`}
                            {competitionScore &&
                              ` · Score ${competitionScore.totalScore.toLocaleString()} / ${competitionScore.maxScore.toLocaleString()}`}
                          </span>
                          {published && (
                            <Chip
                              label="Published"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                          )}
                          {!published && !isDraft && (
                            <Chip
                              label="Private"
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                          )}
                          {isCompetition && !isDraft && (
                            <Chip
                              label={TOP_64_COMPETITION_SHORT_LABEL}
                              size="small"
                              variant="outlined"
                              className="your-ranking-competition-chip"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="your-ranking-card-actions">
                        {published ? (
                          <Button
                            component={Link}
                            to={viewPath}
                            variant="outlined"
                            size="small"
                            className="your-ranking-preview-btn"
                          >
                            View
                          </Button>
                        ) : (
                          <>
                            <Button
                              component={Link}
                              to={viewPath}
                              variant="outlined"
                              size="small"
                              className="your-ranking-preview-btn"
                            >
                              {isDraft ? "Preview" : "View"}
                            </Button>
                            <Button
                              component={Link}
                              to={editPath}
                              variant={isDraft ? "contained" : "outlined"}
                              size="small"
                              disableElevation={isDraft}
                              className={
                                isDraft
                                  ? "your-ranking-edit-btn"
                                  : "your-ranking-preview-btn"
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="outlined"
                              size="small"
                              className="your-ranking-delete-btn"
                              disabled={isDeleting}
                              onClick={() => handleDelete(r)}
                            >
                              {isDeleting ? "Deleting…" : "Delete"}
                            </Button>
                          </>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </Grid>
          )}
        </Grid>
      </Container>
      </div>
    </>
  );
}

function YourRankings() {
  return (
    <RequireAuth>
      <YourRankingsContent />
    </RequireAuth>
  );
}

export default YourRankings;
